<template>
	<div class="analytics-scope-bar flex flex-col gap-3">
		<div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
			<div class="flex flex-col gap-2 sm:flex-row sm:items-center">
				<!-- Segments size to their labels from sm up; below sm they share the row equally. -->
				<TabGroup type="custom" :custom-classes="TAB_GROUP_CANVAS_CLASS" data-testid="analytics-scope-tabs">
					<TabItem
						v-for="s in scopes"
						:key="s.key"
						:label="s.label"
						:is-active="scope === s.key"
						:disabled="disabled"
						:custom-classes="tabItemClass(scope === s.key, { disabled, fill: true })"
						class="min-w-0 flex-1 sm:flex-none"
						@click="$emit('update:scope', s.key)"
					/>
				</TabGroup>
				<FormButton type="secondary" size="md" class="w-full sm:w-auto justify-center" :disabled="disabled" data-testid="analytics-content-picker" @click="$emit('open-picker')">
					<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16" class="mr-1.5 text-surface-8">
						<icon-film />
					</icon-base>
					{{ pickerLabel }}
					<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16" class="ml-1.5 text-surface-7">
						<icon-chevron-down />
					</icon-base>
				</FormButton>
			</div>
			<p class="mb-0 text-[11px] leading-4 text-surface-6 lg:max-w-md lg:text-right">
				Player analytics include playback in the Castr player only. Views on YouTube, Facebook, and other destinations are not included.
			</p>
		</div>

		<div v-if="showChips" class="flex flex-wrap items-center gap-1.5">
			<span
				v-for="item in visibleItems"
				:key="item.id"
				class="group inline-flex h-7 max-w-[240px] items-center gap-1.5 rounded-md bg-surface-3 pl-2 pr-1 text-xs text-surface-text-1"
				:class="{ 'opacity-50': !inScope(item) }"
				:title="item.nameResolving ? undefined : inScope(item) ? item.name : `${item.name} (hidden by the current scope)`"
			>
				<span :class="typeDotClass(item.type)" :title="typeLabel(item.type)" aria-hidden="true" />
				<span v-if="item.nameResolving" class="h-3 w-20 animate-pulse rounded bg-surface-4/80" aria-busy="true" aria-label="Loading name" data-testid="analytics-name-skeleton" />
				<span v-else class="truncate">{{ item.name }}</span>
				<button
					type="button"
					class="flex size-5 shrink-0 items-center justify-center rounded text-surface-7 hover:bg-surface-4 hover:text-white"
					:aria-label="`Remove ${item.name}`"
					:disabled="disabled"
					@click="$emit('remove', item.id)"
				>
					<icon-base :width="10" :height="10" width-view-box="16" height-view-box="16">
						<icon-cross />
					</icon-base>
				</button>
			</span>
			<button v-if="hiddenCount > 0" type="button" class="h-7 rounded-md px-2 text-xs text-surface-8 hover:text-white" @click="expanded = true">
				+{{ hiddenCount }} more
			</button>
			<button v-if="items.length > 1" type="button" class="h-7 rounded-md px-2 text-xs text-surface-7 hover:text-white" :disabled="disabled" @click="$emit('clear')">
				Clear
			</button>
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import TabGroup from '@/components/Atoms/TabGroup.vue';
import TabItem from '@/components/Atoms/TabItem.vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconFilm from '@/components/icon/IconFilm.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import IconCross from '@/components/icon/IconCross.vue';
import { TAB_GROUP_CANVAS_CLASS, tabItemClass } from './analyticsTabs';
import { typeDotClass, typeLabel } from './analyticsTypeBadge';

const props = defineProps({
	scope: { type: String, default: 'all' },
	scopes: { type: Array, required: true },
	// Resolved content items: [{ id, name, type, nameResolving? }]
	items: { type: Array, default: () => [] },
	// True while the default "most recent" selection is in use.
	isDefaultSelection: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
});

defineEmits(['update:scope', 'open-picker', 'remove', 'clear']);

const CHIP_LIMIT = 6;
const expanded = ref(false);

const pickerLabel = computed(() => {
	if (!props.items.length) return 'Choose content';
	if (props.isDefaultSelection) return 'All recent content';
	return `Content: ${props.items.length} selected`;
});

const showChips = computed(() => props.items.length > 0 && !props.isDefaultSelection);
const visibleItems = computed(() => (expanded.value ? props.items : props.items.slice(0, CHIP_LIMIT)));
const hiddenCount = computed(() => (expanded.value ? 0 : Math.max(0, props.items.length - CHIP_LIMIT)));

function inScope(item) {
	return props.scope === 'all' || item.type === props.scope;
}
</script>
